import { isQrCaptureType } from "types";
import {
  StepCaptureData,
  StepDetailsData,
} from "contexts/MultiStepCaptureContext";
import {
  getStepSchemaFingerprint,
  getTaskStepDetailFields,
  getTaskSteps,
  TaskStep,
} from "lib/task-steps";
import {
  getStoredWorkTaskSharedDetails,
  isWorkTaskStepComplete,
  getWorkTaskStepSchemaFingerprint,
} from "lib/work-task-navigation";
import { ResolvedWorkTask } from "lib/work-tasks";
import { captureDB } from "lib/indexed-db";
import { getCurrentSessionId } from "lib/session";

export interface CompletedTaskSteps {
  signature: string;
  stepNumbers: number[];
}

interface TaskProgressInput {
  flow: ResolvedWorkTask | null;
  instructions: unknown;
  captureType: string;
  captures: StepCaptureData[];
  details: StepDetailsData[];
}

export async function saveCompletedTaskSteps(
  taskId: string,
  collectionId: string | undefined,
  steps: CompletedTaskSteps
): Promise<void> {
  const sessionId = await getCurrentSessionId(collectionId);
  if (!sessionId)
    throw new Error("Cannot save step progress without a signed-in session.");
  await captureDB.saveCompletedSteps(sessionId, taskId, steps);
}

export function isPhoneTaskStepComplete(
  step: TaskStep,
  instructions: unknown,
  captureType: string | undefined,
  captures: StepCaptureData[]
): boolean {
  const capture = captures.find(
    (value) => value.stepNumber === step.stepNumber
  );
  return Boolean(
    capture &&
      capture.schemaFingerprint ===
        getStepSchemaFingerprint({
          captureType: captureType as StepCaptureData["captureType"],
          numberOfCaptures: step.numberOfCaptures,
          fields: getTaskStepDetailFields(instructions, step.stepNumber),
          stepLabel: step.configuredStepLabel,
        }) &&
      (capture.isComplete ?? capture.files.length >= capture.numberOfCaptures)
  );
}

export function getCompletedTaskSteps({
  flow,
  instructions,
  captureType,
  captures,
  details,
}: TaskProgressInput): CompletedTaskSteps {
  const steps = flow?.steps ?? getTaskSteps(instructions);
  const signature = JSON.stringify(
    flow
      ? flow.steps.map((step) => ({
          stepNumber: step.stepNumber,
          captureTaskId: step.captureTaskId,
          captureTaskStepNumber: step.captureTaskStepNumber,
          ...(isQrCaptureType(step.captureType)
            ? { orbExtension: step.orbExtension }
            : {}),
          schema: getWorkTaskStepSchemaFingerprint(step),
        }))
      : steps.map((step) => ({
          stepNumber: step.stepNumber,
          schema: getStepSchemaFingerprint({
            captureType: captureType as StepCaptureData["captureType"],
            numberOfCaptures: step.numberOfCaptures,
            fields: getTaskStepDetailFields(instructions, step.stepNumber),
            stepLabel: step.configuredStepLabel,
          }),
        }))
  );
  const sharedDetails = flow
    ? getStoredWorkTaskSharedDetails(flow, captures)
    : null;
  const stepNumbers = flow
    ? flow.steps
        .filter((step) =>
          isWorkTaskStepComplete(step, captures, details, sharedDetails)
        )
        .map((step) => step.stepNumber)
    : steps
        .filter((step) =>
          isPhoneTaskStepComplete(step, instructions, captureType, captures)
        )
        .map((step) => step.stepNumber);
  return { signature, stepNumbers };
}

export function getTaskStepProgress(
  input: TaskProgressInput & { completedSteps?: CompletedTaskSteps | null }
) {
  const steps = input.flow?.steps ?? getTaskSteps(input.instructions);
  // Legacy capture/upload pages without step slices represent one task step.
  const total = steps.length || 1;
  const current = getCompletedTaskSteps(input);
  const saved = input.completedSteps;
  // An active draft always supplies its own progress, including redo/incomplete steps.
  const stepNumbers =
    !input.captures.some((capture) => capture.stepNumber > 0) &&
    saved?.signature === current.signature
      ? saved.stepNumbers
      : current.stepNumbers;
  const validSteps = new Set(
    steps.length ? steps.map((step) => step.stepNumber) : [1]
  );
  return {
    completed: new Set(stepNumbers.filter((step) => validSteps.has(step))).size,
    total,
  };
}
